import { useEffect, useState } from 'react';
import { Card, Row, Col, Spinner, Button, Form, Table, Badge, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useDonationContextProvider } from 'context/DonationContextProvider';
import { supabase } from '../../lib/supabase';
import { pdf } from '@react-pdf/renderer';
import { DonationInvoice } from './DonationInvoice';
import { DonationHeader } from './DonationHeader';
import { DonationFooter } from './DonationFooter';
import { formatMoney } from 'helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faPen,
  faIdCard,
  faMapMarkerAlt,
  faSyncAlt,
  faChild,
  faReceipt,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

function SectionCard({ title, icon, children }) {
  return (
    <Card className="mb-3 border-0 profile-section-card">
      <Card.Header className="bg-white d-flex align-items-center gap-2 profile-section-header">
        <span className="profile-section-icon"><FontAwesomeIcon icon={icon} /></span>
        <h5 className="mb-0 wr-donation-header3">{title}</h5>
      </Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
}

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'Sheikh', 'Imam'];

const ORDERS_QUERY = `
  id, simple_id, donation_amount, payment_status, created_at,
  gift_aid_eligible, subtotal, transaction_fees,
  stripe_invoice_id, branch_id, organisation_id,
  payment_method:payment_method_id ( payment_name ),
  order_details (
    amount, billing_condition,
    donation_type:donation_type_id ( donation_type_name ),
    package:package_id ( package_title )
  )
`;

const SPONSORSHIP_QUERY = `
  id, stipulation, theme, duration, renewal, start_date, created_at,
  orphan:orphan_id ( id, first_name, last_name, country, image, gender, DOB )
`;

/* -------------------------------------------------------------------------- */
/*                            Helper Components                               */
/* -------------------------------------------------------------------------- */

function PageShell({ children }) {
  return (
    <div className="bg-wr-donation-grey-50">
      <DonationHeader />
      <div className="container py-6">{children}</div>
      <DonationFooter />
    </div>
  );
}

function ResendVerificationButton({ email }) {
  const [isSending, setIsSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (!email) {
      toast.error('No email address found');
      return;
    }
    setIsSending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: `${window.location.origin}/` }
      });
      if (error) throw new Error(error.message);
      toast.success('Verification email sent! Check your inbox.');
      setCooldown(60);
    } catch (err) {
      toast.error(err.message || 'Failed to resend verification email');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Button
      className="bg-wr-donation-primary border-0 mt-3"
      onClick={handleResend}
      disabled={isSending || cooldown > 0}
    >
      {isSending ? <Spinner size="sm" /> : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Verification Email'}
    </Button>
  );
}

function ProfileField({ label, value, isEditing, children }) {
  return (
    <Col md={6}>
      <div className="text-muted small">{label}</div>
      {isEditing ? children : <div>{value || '—'}</div>}
    </Col>
  );
}

function SubscriptionsTable({ subscriptions, isCancelling, onCancel }) {
  if (subscriptions.length === 0) {
    return <p className="text-muted mb-0">No active subscriptions.</p>;
  }

  return (
    <Table responsive hover className="profile-table">
      <thead>
        <tr>
          <th>Items</th>
          <th className="text-end">Amount</th>
          <th>Frequency</th>
          <th>Status</th>
          <th>Started</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {subscriptions.map(sub => (
          <tr key={sub.id}>
            <td>
              {sub.order_details_snapshot?.map((detail, i) => (
                <div key={i} className="small">
                  {detail.package_title || detail.donation_type_name || 'Monthly Donation'}
                </div>
              )) || 'Monthly Donation'}
            </td>
            <td className="text-end">{formatMoney(sub.amount, '£')}</td>
            <td>Monthly</td>
            <td>
              <Badge bg={sub.status === 'active' ? 'success' : sub.status === 'cancelled' ? 'danger' : 'warning'} className="profile-badge">
                {sub.status}
              </Badge>
            </td>
            <td>{new Date(sub.created_at).toLocaleDateString()}</td>
            <td>
              {sub.status === 'active' && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onCancel(sub.id)}
                  disabled={isCancelling === sub.id}
                >
                  {isCancelling === sub.id ? <Spinner size="sm" /> : 'Cancel'}
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function SponsorshipsTable({ sponsorships }) {
  if (sponsorships.length === 0) {
    return <p className="text-muted mb-0">No sponsorships yet.</p>;
  }

  return (
    <Table responsive hover className="profile-table">
      <thead>
        <tr>
          <th>Beneficiary</th>
          <th>Country</th>
          <th>Theme</th>
          <th>Duration</th>
          <th>Renewal</th>
          <th>Start Date</th>
        </tr>
      </thead>
      <tbody>
        {sponsorships.map(s => (
          <tr key={s.id}>
            <td>
              <div className="d-flex align-items-center gap-2">
                {s.orphan?.image && (
                  <img
                    src={s.orphan.image}
                    alt=""
                    style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }}
                  />
                )}
                <span>{s.orphan?.first_name} {s.orphan?.last_name}</span>
              </div>
            </td>
            <td>{s.orphan?.country || '—'}</td>
            <td>{s.theme || '—'}</td>
            <td>{s.duration || '—'}</td>
            <td>{s.renewal || '—'}</td>
            <td>{s.start_date ? new Date(s.start_date).toLocaleDateString() : '—'}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function OrdersTable({ orders, onDownloadInvoice }) {
  if (orders.length === 0) {
    return <p className="text-muted mb-0">No donations yet.</p>;
  }

  return (
    <Table responsive hover className="profile-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Reference</th>
          <th>Items</th>
          <th className="text-end">Amount</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {orders.map(order => (
          <tr key={order.id}>
            <td>{new Date(order.created_at).toLocaleDateString()}</td>
            <td>{order.simple_id || '—'}</td>
            <td>
              {order.order_details?.map((detail, i) => (
                <div key={i} className="small">
                  {detail.package?.package_title || detail.donation_type?.donation_type_name || 'Donation'}
                  {detail.billing_condition === 'monthly' && <Badge bg={null} className="profile-badge bg-wr-donation-primary ms-1">Monthly</Badge>}
                </div>
              )) || '—'}
            </td>
            <td className="text-end">{formatMoney(order.donation_amount, '£')}</td>
            <td>
              <Badge bg={order.payment_status === 'paid' ? 'success' : 'warning'} className="profile-badge">
                {order.payment_status}
              </Badge>
            </td>
            <td>
              {order.payment_status === 'paid' && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onDownloadInvoice(order)}
                  title="Download Receipt"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Main Component                                */
/* -------------------------------------------------------------------------- */

function ProfilePage() {
  const { donor, setDonor } = useDonationContextProvider();
  const [donorRecord, setDonorRecord] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isCancelling, setIsCancelling] = useState(null);
  const [cancelConfirmId, setCancelConfirmId] = useState(null);
  const [authEmail, setAuthEmail] = useState(null);

  const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

  const fetchDonor = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const user = sessionData?.session?.user;

    if (!user) {
      setIsLoading(false);
      return;
    }

    const email = user.email;
    setAuthEmail(email);
    setIsEmailVerified(!!user.email_confirmed_at || !!user.confirmed_at);

    const { data, error } = await supabase
      .from('donars')
      .select('*')
      .eq('organisation_id', organizationId)
      .ilike('primary_email', email)
      .limit(1);

    if (error) {
      console.error('Failed to look up donor record:', error);
      toast.error('We could not load your profile right now. Please try again shortly.');
    } else if (data?.length > 0) {
      setDonorRecord(data[0]);
      const donorId = data[0].id;

      const [ordersResult, subscriptionsResult, sponsorshipsResult] = await Promise.all([
        supabase
          .from('orders')
          .select(ORDERS_QUERY)
          .eq('donar_id', donorId)
          .order('created_at', { ascending: false })
          .limit(100)
          .abortSignal(AbortSignal.timeout(10000)),
        supabase
          .from('recurring_donations')
          .select('*')
          .eq('donor_id', donorId)
          .order('created_at', { ascending: false })
          .limit(50)
          .abortSignal(AbortSignal.timeout(10000)),
        supabase
          .from('sponsorship')
          .select(SPONSORSHIP_QUERY)
          .eq('donar_id', donorId)
          .order('created_at', { ascending: false })
          .limit(50)
          .abortSignal(AbortSignal.timeout(10000))
      ]);

      const failedSections = [];
      if (ordersResult.error) {
        console.error('Failed to load donation history:', ordersResult.error);
        failedSections.push('donation history');
      }
      if (subscriptionsResult.error) {
        console.error('Failed to load subscriptions:', subscriptionsResult.error);
        failedSections.push('subscriptions');
      }
      if (sponsorshipsResult.error) {
        console.error('Failed to load sponsorships:', sponsorshipsResult.error);
        failedSections.push('sponsorships');
      }
      if (failedSections.length > 0) {
        toast.error(`We couldn't load your ${failedSections.join(', ')}. Please refresh or try again later.`);
      }

      setOrders(ordersResult.data || []);
      setSubscriptions(subscriptionsResult.data || []);
      setSponsorships(sponsorshipsResult.data || []);
    }
    setIsLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDonor(); }, []);

  const startEditing = () => {
    const d = donorRecord || donor;
    setFormData({
      title: d?.title || '',
      first_name: d?.first_name || d?.firstName || '',
      last_name: d?.last_name || d?.lastName || '',
      primary_phone: d?.primary_phone || d?.phoneNumber || '',
      addressLine1: d?.address?.[0]?.addressLine1 || d?.addressLine1 || '',
      addressLine2: d?.address?.[0]?.addressLine2 || d?.addressLine2 || '',
      city: d?.address?.[0]?.city || d?.city || '',
      postcode: d?.address?.[0]?.postcode || d?.postcode || '',
      country: d?.address?.[0]?.country || d?.country || ''
    });
    setIsEditing(true);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!donorRecord?.id) {
      toast.error('No donor record found to update');
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('donars')
        .update({
          title: formData.title,
          first_name: formData.first_name,
          last_name: formData.last_name,
          primary_phone: formData.primary_phone,
          phone: [{ phoneNumber: formData.primary_phone, isPrimary: true }],
          address: [{
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            postcode: formData.postcode,
            country: formData.country,
            isPrimary: true
          }],
          primary_address: formData.addressLine1
        })
        .eq('id', donorRecord.id);

      if (error) throw new Error(error.message);

      await fetchDonor();
      setDonor(prev => ({
        ...prev,
        title: formData.title,
        firstName: formData.first_name,
        lastName: formData.last_name,
        phoneNumber: formData.primary_phone,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        postcode: formData.postcode,
        country: formData.country
      }));
      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const cancelSubscription = async (sub) => {
    setCancelConfirmId(null);
    setIsCancelling(sub.id);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: {
          subscriptionId: sub.stripe_subscription_id,
          branchId: sub.branch_id,
          organisationId: sub.organisation_id
        }
      });

      if (error) {
        const errorBody = typeof error === 'object' && error.context
          ? await error.context.json().catch(() => null)
          : null;
        throw new Error(errorBody?.error || error?.message || 'Failed to cancel subscription');
      }
      if (data?.error) throw new Error(data.error);

      toast.success('Subscription has been cancelled.');
      await fetchDonor();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel subscription');
    } finally {
      setIsCancelling(null);
    }
  };

  // Generates the branded receipt in the browser — same layout as the one
  // emailed on payment. Triggered via an anchor download rather than
  // window.open so popup blockers can't swallow it.
  const downloadInvoice = async (order) => {
    try {
      const blob = await pdf(<DonationInvoice order={order} donor={donorRecord} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${order.simple_id || order.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate receipt:', err);
      toast.error('Could not generate the receipt. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <PageShell>
        <div className="d-flex justify-content-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" />
        </div>
      </PageShell>
    );
  }

  if (!isEmailVerified) {
    return (
      <PageShell>
        <Card className="text-center py-5">
          <Card.Body>
            <h4 className="mb-3">Email Verification Required</h4>
            <p className="text-muted">
              Please verify your email address to view your profile details, donation history, and subscriptions.
            </p>
            <p className="text-muted small">Check your inbox for a verification link from us.</p>
            <ResendVerificationButton email={donor?.email || authEmail} />
          </Card.Body>
        </Card>
      </PageShell>
    );
  }

  if (!donorRecord) {
    return (
      <PageShell>
        <Card className="text-center py-5">
          <Card.Body>
            <h4 className="mb-3">No Donation History Found</h4>
            <p className="text-muted">Please complete a donation before accessing your profile page.</p>
            <Button
              className="bg-wr-donation-primary border-0 mt-3"
              onClick={() => window.location.href = '/'}
            >
              Make a Donation
            </Button>
          </Card.Body>
        </Card>
      </PageShell>
    );
  }

  const d = donorRecord;
  const fullName = [d?.title, d?.first_name, d?.last_name].filter(Boolean).join(' ');
  const initials = `${d?.first_name?.[0] || ''}${d?.last_name?.[0] || ''}`.toUpperCase() || 'D';

  return (
    <PageShell>
      <style>{`
        .profile-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background-color: #012f86;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Oswald', sans-serif;
          font-size: 22px;
          font-weight: 600;
          flex: none;
        }
        .profile-section-card {
          box-shadow: 0 2px 7px 0 #0000001c;
          border-radius: 8px;
          overflow: hidden;
        }
        .profile-section-header {
          border-bottom: 1px solid #e6ebf3 !important;
          padding: 16px 20px;
        }
        .profile-section-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: #f8faff;
          color: #012f86;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          flex: none;
        }
        .profile-verified-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #2aa316;
        }
        .profile-table thead th {
          background-color: #f8faff;
          color: #637381;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          border-bottom: 1px solid #e6ebf3;
          white-space: nowrap;
        }
        .profile-table td {
          vertical-align: middle;
          font-size: 14px;
        }
        .profile-badge {
          font-weight: 600;
          text-transform: capitalize;
          padding: 5px 10px;
          border-radius: 6px;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
        <div className="d-flex align-items-center gap-3">
          <span className="profile-avatar">{initials}</span>
          <div>
            <h3 className="mb-1 wr-donation-header4">{fullName || 'My Profile'}</h3>
            <div className="text-muted small mb-1">{d?.primary_email || d?.email}</div>
            <span className="profile-verified-badge">
              <FontAwesomeIcon icon={faCheck} /> Verified account
            </span>
          </div>
        </div>
        {!isEditing ? (
          <Button className="bg-wr-donation-primary border-0 d-flex align-items-center gap-2" onClick={startEditing}>
            <FontAwesomeIcon icon={faPen} /> Edit Profile
          </Button>
        ) : (
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => setIsEditing(false)} disabled={isSaving}>Cancel</Button>
            <Button className="bg-wr-donation-primary border-0" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Spinner size="sm" /> : 'Save'}
            </Button>
          </div>
        )}
      </div>

      <SectionCard title="Personal Information" icon={faIdCard}>
        <Row className="g-3">
          <ProfileField label="Title" value={d?.title} isEditing={isEditing}>
            <Form.Select value={formData.title} onChange={e => handleChange('title', e.target.value)}>
              <option value="">Select</option>
              {TITLE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </Form.Select>
          </ProfileField>
          <ProfileField label="Email" value={d?.primary_email || d?.email} isEditing={false} />
          <ProfileField label="First Name" value={d?.first_name} isEditing={isEditing}>
            <Form.Control value={formData.first_name} onChange={e => handleChange('first_name', e.target.value)} />
          </ProfileField>
          <ProfileField label="Last Name" value={d?.last_name} isEditing={isEditing}>
            <Form.Control value={formData.last_name} onChange={e => handleChange('last_name', e.target.value)} />
          </ProfileField>
          <ProfileField label="Phone" value={d?.primary_phone} isEditing={isEditing}>
            <Form.Control value={formData.primary_phone} onChange={e => handleChange('primary_phone', e.target.value)} />
          </ProfileField>
        </Row>
      </SectionCard>

      <SectionCard title="Address" icon={faMapMarkerAlt}>
        <Row className="g-3">
          <ProfileField label="Address Line 1" value={d?.address?.[0]?.addressLine1} isEditing={isEditing}>
            <Form.Control value={formData.addressLine1} onChange={e => handleChange('addressLine1', e.target.value)} />
          </ProfileField>
          <ProfileField label="Address Line 2" value={d?.address?.[0]?.addressLine2} isEditing={isEditing}>
            <Form.Control value={formData.addressLine2} onChange={e => handleChange('addressLine2', e.target.value)} />
          </ProfileField>
          <ProfileField label="City" value={d?.address?.[0]?.city} isEditing={isEditing}>
            <Form.Control value={formData.city} onChange={e => handleChange('city', e.target.value)} />
          </ProfileField>
          <ProfileField label="Postcode" value={d?.address?.[0]?.postcode} isEditing={isEditing}>
            <Form.Control value={formData.postcode} onChange={e => handleChange('postcode', e.target.value)} />
          </ProfileField>
          <ProfileField label="Country" value={d?.address?.[0]?.country} isEditing={isEditing}>
            <Form.Control value={formData.country} onChange={e => handleChange('country', e.target.value)} />
          </ProfileField>
        </Row>
      </SectionCard>

      <SectionCard title="Subscriptions" icon={faSyncAlt}>
        <SubscriptionsTable
          subscriptions={subscriptions}
          isCancelling={isCancelling}
          onCancel={setCancelConfirmId}
        />
      </SectionCard>

      <SectionCard title="Sponsorships" icon={faChild}>
        <SponsorshipsTable sponsorships={sponsorships} />
      </SectionCard>

      <SectionCard title="Donation History" icon={faReceipt}>
        <OrdersTable orders={orders} onDownloadInvoice={downloadInvoice} />
      </SectionCard>

      <Modal show={!!cancelConfirmId} onHide={() => setCancelConfirmId(null)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title>Cancel Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to cancel this subscription?</p>
          <p className="text-muted small mb-0">Your subscription will be cancelled immediately.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" onClick={() => setCancelConfirmId(null)}>Keep Subscription</Button>
          <Button
            variant="danger"
            onClick={() => {
              const sub = subscriptions.find(s => s.id === cancelConfirmId);
              if (sub) cancelSubscription(sub);
            }}
          >
            Yes, Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}

export { ProfilePage };
